import Spinner from './ui/spinner';
import { useState, useEffect } from 'react';
import useDebounce from '../hooks/useDebounce';
import useListParams from '../hooks/useListParams';
import {
  Search,
  Clock,
  AlertTriangle,
  CheckCircle,
  CircleCheckBig,
  X,
  XCircle,
  MoreVertical,
  Shield,
  Flag,
} from 'lucide-react';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import { IReport, IReportStats, IPaginated } from '../types';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { getImageUrl } from '../utils/imageUrl';
import { getReasonLabel } from '../utils/reportLabels';
import Pagination from './Pagination';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import ImagePreviewOverlay from './ImagePreviewOverlay';

type EFilter = 'all' | 'pending' | 'reviewing' | 'resolved' | 'rejected';

const ReportManagement = ({
  onNavigateUser,
}: {
  onNavigateUser?: (userId: string) => void;
}) => {
  const { t } = useTranslation();

  const TYPE_LABELS: Record<string, { label: string }> = {
    harassment: { label: t('reports.type_harassment') },
    spam: { label: t('reports.type_spam') },
    inappropriate: { label: t('reports.type_inappropriate') },
    violence: { label: t('reports.type_violence') },
    other: { label: t('reports.type_other') },
  };

  const [reports, setReports] = useState<IReport[]>([]);
  const [stats, setStats] = useState<IReportStats | null>(null);
  const { search, filter, page, setSearch, setFilter, setPage } = useListParams(
    { filter: 'all' },
  );
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<IReport | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_LIMIT = 20;

  // Confirmation modal state
  const [warnTarget, setWarnTarget] = useState<IReport | null>(null);
  const [warnReason, setWarnReason] = useState('');
  const [banTarget, setBanTarget] = useState<IReport | null>(null);
  const [banReason, setBanReason] = useState('');
  const [muteTarget, setMuteTarget] = useState<IReport | null>(null);
  const [muteDuration, setMuteDuration] = useState('');
  const [rejectTarget, setRejectTarget] = useState<IReport | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    loadData(page);
  }, [filter, page]);

  // Debounced search on type
  useDebounce(
    () => {
      loadData(1);
    },
    300,
    [search],
  );

  const loadData = async (p = page) => {
    try {
      const params = new URLSearchParams();
      params.append('status', filter || 'all');

      if (search) params.append('search', search);
      params.append('page', String(p));
      params.append('limit', String(PAGE_LIMIT));
      const [r, s] = await Promise.all([
        api.get<IPaginated<IReport>>(
          `/backoffice/reports?${params.toString()}`,
        ),
        api.get<IReportStats>('/backoffice/reports/stats'),
      ]);
      setReports(r.data || []);
      setTotal(r.total);
      setTotalPages(r.totalPages);
      setStats(s);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      await api.patch(`/backoffice/reports/${id}/status`, { status });
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleWarnUser = async (id: string, reason?: string) => {
    if (actionLoading) {
      return;
    }

    setActionLoading(true);

    try {
      await api.post(`/backoffice/reports/${id}/warn`, { reason });
      loadData();
      setSelected(null);
      setWarnTarget(null);
      setWarnReason('');
    } catch (err: any) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBanUser = async (id: string, reason?: string) => {
    if (actionLoading) {
      return;
    }

    setActionLoading(true);

    try {
      const report = banTarget;

      // Ban the reported user first
      if (report?.reportedUserId) {
        await api.post(`/admin/users/${report.reportedUserId}/ban`, {
          reason,
        });
      }

      // Then resolve the report
      await api.patch(`/backoffice/reports/${id}/status`, {
        status: 'resolved',
      });
      loadData();
      setSelected(null);
      setBanTarget(null);
      setBanReason('');
    } catch (err: any) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMuteUser = async (_id: string, duration: string) => {
    if (actionLoading) {
      return;
    }

    setActionLoading(true);

    try {
      const report = muteTarget;

      if (report?.reportedUserId) {
        let mutedUntil: string | undefined;

        if (duration && duration !== 'permanent') {
          const now = new Date();
          const durationMap: Record<string, number> = {
            '1day': 1,
            '3days': 3,
            '1week': 7,
            '1month': 30,
          };
          const days = durationMap[duration] || 1;

          now.setDate(now.getDate() + days);
          mutedUntil = now.toISOString();
        }

        await api.post(`/admin/users/${report.reportedUserId}/mute`, {
          reason: t('reports.mute_user'),
          mutedUntil,
        });
      }

      loadData();
      setSelected(null);
      setMuteTarget(null);
      setMuteDuration('');
    } catch (err: any) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Spinner />;

  const filters: { key: EFilter; label: string }[] = [
    { key: 'all', label: t('common.all') },
    { key: 'pending', label: t('reports.pending') },
    { key: 'reviewing', label: t('reports.reviewing') },
    { key: 'resolved', label: t('reports.resolved') },
  ];

  const STAT_ICONS: Record<string, { icon: React.ElementType; color: string }> =
    {
      pending: { icon: Clock, color: 'text-yellow-600' },
      reviewing: { icon: AlertTriangle, color: 'text-blue-600' },
      resolved: { icon: CircleCheckBig, color: 'text-green-600' },
      rejected: { icon: XCircle, color: 'text-gray-600' },
    };

  type TBadgeVariant = 'warning' | 'info' | 'success' | 'muted';

  const STATUS_BADGE: Record<
    string,
    { variant: TBadgeVariant; icon: React.ElementType | null; label: string }
  > = {
    pending: {
      variant: 'warning',
      icon: Clock,
      label: t('reports.pending'),
    },
    reviewing: {
      variant: 'info',
      icon: AlertTriangle,
      label: t('reports.reviewing'),
    },
    resolved: {
      variant: 'success',
      icon: CheckCircle,
      label: t('reports.resolved'),
    },
    rejected: {
      variant: 'muted',
      icon: XCircle,
      label: t('reports.rejected'),
    },
  };

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {t('reports.title')}
        </h1>
        <p className="text-base text-gray-600 mt-2">{t('reports.subtitle')}</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            {
              label: t('reports.pending'),
              value: stats.pending,
              key: 'pending',
            },
            {
              label: t('reports.reviewing'),
              value: stats.reviewing,
              key: 'reviewing',
            },
            {
              label: t('reports.resolved'),
              value: stats.resolved,
              key: 'resolved',
            },
            {
              label: t('reports.rejected'),
              value: stats.rejected,
              key: 'rejected',
            },
          ].map(({ label, value, key }) => {
            const { icon: Icon, color } = STAT_ICONS[key];

            return (
              <Card key={label}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{label}</p>
                      <p
                        className="text-2xl font-bold text-gray-900 mt-1"
                        data-testid={`ReportManagement_List_${key}CountText`}
                      >
                        {value}
                      </p>
                    </div>
                    <Icon className={`w-8 h-8 ${color}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Search + Filter */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('reports.search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="ReportManagement_List_searchInput"
              />
            </div>
            <div className="flex gap-2">
              {filters.map(({ key, label }) => (
                <Button
                  key={key}
                  variant={filter === key ? 'default' : 'outline'}
                  onClick={() => setFilter(key)}
                  data-testid={`ReportManagement_List_filterButton_${key}`}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reports.reporter')}</TableHead>
                <TableHead>{t('reports.reported_user')}</TableHead>
                <TableHead>{t('reports.group')}</TableHead>
                <TableHead>{t('reports.type')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead>{t('reports.time')}</TableHead>
                <TableHead className="text-right">
                  {t('common.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((report) => {
                const sb = STATUS_BADGE[report.status] || STATUS_BADGE.pending;
                const StatusIcon = sb.icon;

                return (
                  <TableRow
                    key={report.id}
                    onClick={() => setSelected(report)}
                    className="cursor-pointer hover:bg-gray-50"
                    data-testid={`ReportManagement_List_reportRow_${report.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {report.reporter?.avatar ? (
                          <img
                            src={getImageUrl(report.reporter.avatar)}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {(report.reporter?.displayName || '?')
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm text-blue-600">
                          {report.reporter?.displayName || '—'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {report.reportedUser?.avatar ? (
                          <img
                            src={getImageUrl(report.reportedUser.avatar)}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {(report.reportedUser?.displayName || '?')
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm font-medium text-blue-600">
                          {report.reportedUser?.displayName || '—'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">
                        {report.room?.name || report.space?.name || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {(TYPE_LABELS[report.type] || TYPE_LABELS.other).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={sb.variant}>
                        {StatusIcon && <StatusIcon className="w-3 h-3 mr-1" />}
                        {sb.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-gray-500">
                        {formatDateTime(report.createdAt)}
                      </span>
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`ReportManagement_List_rowActionsButton_${report.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem
                            onClick={() => setSelected(report)}
                            data-testid={`ReportManagement_List_viewDetailButton_${report.id}`}
                          >
                            {t('reports.view_detail')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(report.id, 'reviewing')
                            }
                            data-testid={`ReportManagement_List_markInProgressButton_${report.id}`}
                          >
                            {t('reports.mark_in_progress')}
                          </DropdownMenuItem>
                          {/* Room reports have no reportee — user actions
                              would no-op, so they are not offered */}
                          {report.reportedUserId && (
                            <>
                              <DropdownMenuItem
                                className="text-orange-600"
                                onClick={() => {
                                  setWarnTarget(report);
                                  setWarnReason('');
                                }}
                                data-testid={`ReportManagement_List_sendWarningButton_${report.id}`}
                              >
                                {t('reports.send_warning_message')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setMuteTarget(report);
                                  setMuteDuration('');
                                }}
                                data-testid={`ReportManagement_List_muteUserButton_${report.id}`}
                              >
                                {t('reports.mute_user')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => {
                                  setBanTarget(report);
                                  setBanReason('');
                                }}
                                data-testid={`ReportManagement_List_suspendUserButton_${report.id}`}
                              >
                                {t('reports.suspend_user')}
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem
                            className="text-gray-600"
                            onClick={() => {
                              setRejectTarget(report);
                              setRejectReason('');
                            }}
                            data-testid={`ReportManagement_List_rejectReportButton_${report.id}`}
                          >
                            {t('reports.reject_report')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
              {reports.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    {t('reports.no_reports')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={PAGE_LIMIT}
            onPageChange={setPage}
            testId="ReportManagement_List"
          />
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-xl border p-6 shadow-lg w-full max-w-2xl h-[85vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="ReportManagement_List_detailModal"
          >
            {/* Header */}
            <div className="flex flex-col gap-2 mb-4">
              <h2 className="text-lg font-semibold leading-none">
                {t('reports.detail_title')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('reports.detail_subtitle')}
              </p>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-5">
                {/* Report Type + Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('reports.report_type')}
                    </p>
                    <p className="text-sm text-gray-900 mt-1 font-medium">
                      {(TYPE_LABELS[selected.type] || TYPE_LABELS.other).label}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('common.status')}
                    </p>
                    <div className="mt-1">
                      {(() => {
                        const sb =
                          STATUS_BADGE[selected.status] || STATUS_BADGE.pending;
                        const StatusIcon = sb.icon;

                        return (
                          <Badge variant={sb.variant}>
                            {StatusIcon && (
                              <StatusIcon className="w-3 h-3 mr-1" />
                            )}
                            {sb.label}
                          </Badge>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Reporter + Reported User */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('reports.reporter')}
                    </p>
                    <button
                      onClick={() => {
                        if (selected.reporterId && onNavigateUser) {
                          setSelected(null);
                          onNavigateUser(selected.reporterId);
                        }
                      }}
                      className="flex items-center gap-2 mt-1"
                      data-testid="ReportManagement_List_detailModalReporterLink"
                    >
                      {selected.reporter?.avatar ? (
                        <img
                          src={getImageUrl(selected.reporter.avatar)}
                          alt=""
                          className="w-7 h-7 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {(selected.reporter?.displayName || '?')
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                        {selected.reporter?.displayName || '—'}
                      </span>
                    </button>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('reports.reported_user')}
                    </p>
                    <button
                      onClick={() => {
                        if (selected.reportedUserId && onNavigateUser) {
                          setSelected(null);
                          onNavigateUser(selected.reportedUserId);
                        }
                      }}
                      className="flex items-center gap-2 mt-1"
                      data-testid="ReportManagement_List_detailModalReportedUserLink"
                    >
                      {selected.reportedUser?.avatar ? (
                        <img
                          src={getImageUrl(selected.reportedUser.avatar)}
                          alt=""
                          className="w-7 h-7 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {(selected.reportedUser?.displayName || '?')
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                        {selected.reportedUser?.displayName || '—'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Related Group */}
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t('reports.related_group')}
                  </p>
                  <p className="text-sm text-gray-900 mt-1 font-medium">
                    {selected.room?.name || selected.space?.name || '—'}
                  </p>
                </div>

                {/* Report Reason */}
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t('reports.report_reason')}
                  </p>
                  <p className="text-sm text-gray-900 mt-1 font-medium">
                    {getReasonLabel(selected.reason, t)}
                  </p>
                </div>

                {/* Detailed Description */}
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t('reports.detail_description')}
                  </p>
                  <p className="text-sm text-gray-900 mt-1 font-medium">
                    {selected?.description || '-'}
                  </p>
                </div>

                {/* Evidence Details (reported message content) */}
                {selected.message?.content && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('reports.evidence')}
                    </p>
                    <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-border whitespace-pre-wrap text-sm text-gray-900">
                      {selected.message.content}
                    </div>
                  </div>
                )}

                {/* Evidence Images - hidden if not available */}
                {selected.message?.imageUrl && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {t('reports.evidence_images')}
                    </p>
                    <div className="mt-1 flex gap-3 flex-wrap">
                      <img
                        src={getImageUrl(selected.message.imageUrl)}
                        alt=""
                        className="w-36 h-36 rounded-lg border border-border object-cover cursor-pointer hover:opacity-90 transition-opacity bg-gray-100"
                        onClick={() =>
                          setPreviewImage(
                            getImageUrl(selected.message!.imageUrl!) || '',
                          )
                        }
                        data-testid="ReportManagement_List_detailModalEvidenceImageButton"
                      />
                    </div>
                  </div>
                )}

                {/* Submit Time */}
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t('reports.submit_time')}
                  </p>
                  <p className="text-sm text-gray-900 mt-1 font-medium">
                    {formatDateTime(selected.createdAt)}
                  </p>
                </div>

                {/* Report History */}
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    {t('reports.report_history')}
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {/* Current report entry - color based on status */}
                    {(() => {
                      const isResolved =
                        selected.status === 'resolved' ||
                        selected.status === 'rejected';
                      const typeLabel = (
                        TYPE_LABELS[selected.type] || TYPE_LABELS.other
                      ).label;
                      const locationName =
                        selected.room?.name || selected.space?.name || '';

                      if (isResolved) {
                        return (
                          <div className="flex items-start gap-3 p-3 border border-green-200 rounded-lg bg-green-50">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-green-900">
                                    {t('reports.reported_label')} - {typeLabel}
                                  </p>
                                  <p className="text-xs text-green-700 mt-1">
                                    {locationName
                                      ? `${t('reports.in_location', { name: locationName })} - `
                                      : ''}
                                    {selected.resolution ||
                                      t('reports.resolved')}
                                  </p>
                                </div>
                                <span className="text-xs text-green-600 whitespace-nowrap">
                                  {formatDate(
                                    selected.resolvedAt || selected.createdAt,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="flex items-start gap-3 p-3 border border-red-200 rounded-lg bg-red-50">
                          <Flag className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-red-900">
                                  {t('reports.reported_label')} - {typeLabel}
                                </p>
                                <p className="text-xs text-red-700 mt-1">
                                  {locationName
                                    ? `${t('reports.in_location', { name: locationName })}${selected.reason ? ' ' + getReasonLabel(selected.reason, t) : ''}`
                                    : getReasonLabel(selected.reason, t)}
                                </p>
                              </div>
                              <span className="text-xs text-red-600 whitespace-nowrap">
                                {formatDate(selected.createdAt)}
                              </span>
                            </div>
                            <Badge
                              variant={
                                (
                                  STATUS_BADGE[selected.status] ||
                                  STATUS_BADGE.pending
                                ).variant
                              }
                              className="mt-1"
                            >
                              {
                                (
                                  STATUS_BADGE[selected.status] ||
                                  STATUS_BADGE.pending
                                ).label
                              }
                            </Badge>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Warning entry - shown if report was resolved with a warning */}
                    {selected.resolution && selected.status === 'resolved' && (
                      <div className="flex items-start gap-3 p-3 border border-orange-200 rounded-lg bg-orange-50">
                        <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-xs font-medium text-orange-900">
                                {t('reports.warning_record')}
                              </p>
                              <p className="text-xs text-orange-700 mt-1">
                                {selected.resolution}
                              </p>
                            </div>
                            <span className="text-xs text-orange-600 whitespace-nowrap">
                              {formatDate(
                                selected.resolvedAt || selected.createdAt,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border mt-4">
              {/* Room reports have no reportee — user actions would no-op,
                  so only rejecting the report is offered */}
              {selected.reportedUserId && (
                <>
                  <Button
                    variant="outline"
                    className="rounded-lg px-5"
                    onClick={() => {
                      setWarnTarget(selected);
                      setWarnReason('');
                    }}
                    data-testid="ReportManagement_List_detailModalWarnUserButton"
                  >
                    {t('reports.warn_user')}
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-lg px-5"
                    onClick={() => {
                      setMuteTarget(selected);
                      setMuteDuration('');
                    }}
                    data-testid="ReportManagement_List_detailModalMuteUserButton"
                  >
                    {t('reports.mute_user')}
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                className="rounded-lg px-5"
                onClick={() => {
                  setRejectTarget(selected);
                  setRejectReason('');
                }}
                data-testid="ReportManagement_List_detailModalRejectReportButton"
              >
                {t('reports.reject_report')}
              </Button>
              {selected.reportedUserId && (
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-5"
                  onClick={() => {
                    setBanTarget(selected);
                    setBanReason('');
                  }}
                  data-testid="ReportManagement_List_detailModalBanUserButton"
                >
                  {t('reports.ban_user')}
                </Button>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
              data-testid="ReportManagement_List_detailModalCloseButton"
            >
              <X className="w-4 h-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>
      )}
      {/* Warn User Modal */}
      {warnTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setWarnTarget(null)}
        >
          <div
            className="bg-white rounded-xl p-6 w-[480px] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
            data-testid="ReportManagement_List_warnModal"
          >
            <h3 className="text-lg font-semibold text-gray-900">
              {t('reports.warn_title')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('reports.warn_desc', {
                name: warnTarget.reportedUser?.displayName || '—',
              })}
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-900">
                    {t('reports.warn_title')}
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {t('reports.warn_info')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.warn_reason_label')}
              </label>
              <Textarea
                value={warnReason}
                onChange={(e) => setWarnReason(e.target.value)}
                placeholder={t('reports.warn_reason_placeholder')}
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                data-testid="ReportManagement_List_warnModalReasonTextArea"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setWarnTarget(null)}
                data-testid="ReportManagement_List_warnModalCancelButton"
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleWarnUser(warnTarget.id, warnReason)}
                disabled={actionLoading}
                data-testid="ReportManagement_List_warnModalConfirmButton"
              >
                <AlertTriangle className="w-4 h-4" />
                {t('reports.confirm_warn')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {banTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setBanTarget(null)}
        >
          <div
            className="bg-white rounded-xl p-6 w-[480px] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
            data-testid="ReportManagement_List_banModal"
          >
            <h3 className="text-lg font-semibold text-gray-900">
              {t('reports.ban_title')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('reports.ban_desc', {
                name: banTarget.reportedUser?.displayName || '—',
              })}
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-900">
                    {t('admin.delete_warning')}
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {t('reports.ban_info')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.ban_reason_label')}
              </label>
              <Textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder={t('reports.ban_reason_placeholder')}
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                data-testid="ReportManagement_List_banModalReasonTextArea"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setBanTarget(null)}
                data-testid="ReportManagement_List_banModalCancelButton"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleBanUser(banTarget.id, banReason)}
                disabled={actionLoading}
                data-testid="ReportManagement_List_banModalConfirmButton"
              >
                <Shield className="w-4 h-4" />
                {t('reports.confirm_ban')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mute User Modal */}
      {muteTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setMuteTarget(null)}
        >
          <div
            className="bg-white rounded-xl p-6 w-[480px] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
            data-testid="ReportManagement_List_muteModal"
          >
            <h3 className="text-lg font-semibold text-gray-900">
              {t('reports.mute_title')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('reports.mute_desc', {
                name: muteTarget.reportedUser?.displayName || '—',
              })}
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-900">
                    {t('reports.mute_title')}
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {t('reports.mute_info')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.mute_duration')}
              </label>
              <Select
                value={muteDuration || 'none'}
                onValueChange={(val) =>
                  setMuteDuration(val === 'none' ? '' : val)
                }
              >
                <SelectTrigger data-testid="ReportManagement_List_muteModalDurationSelect">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value="none"
                    data-testid="ReportManagement_List_muteModalDurationSelectOption_none"
                  >
                    {t('reports.mute_select_duration')}
                  </SelectItem>
                  <SelectItem
                    value="1day"
                    data-testid="ReportManagement_List_muteModalDurationSelectOption_1day"
                  >
                    {t('reports.mute_1day')}
                  </SelectItem>
                  <SelectItem
                    value="3days"
                    data-testid="ReportManagement_List_muteModalDurationSelectOption_3days"
                  >
                    {t('reports.mute_3days')}
                  </SelectItem>
                  <SelectItem
                    value="1week"
                    data-testid="ReportManagement_List_muteModalDurationSelectOption_1week"
                  >
                    {t('reports.mute_1week')}
                  </SelectItem>
                  <SelectItem
                    value="1month"
                    data-testid="ReportManagement_List_muteModalDurationSelectOption_1month"
                  >
                    {t('reports.mute_1month')}
                  </SelectItem>
                  <SelectItem
                    value="permanent"
                    data-testid="ReportManagement_List_muteModalDurationSelectOption_permanent"
                  >
                    {t('reports.mute_permanent')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {muteTarget.room?.name && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600">
                  {t('reports.group')}:{' '}
                  <span className="font-medium text-gray-900">
                    {muteTarget.room.name}
                  </span>
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setMuteTarget(null)}
                data-testid="ReportManagement_List_muteModalCancelButton"
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleMuteUser(muteTarget.id, muteDuration)}
                disabled={!muteDuration || actionLoading}
                data-testid="ReportManagement_List_muteModalConfirmButton"
              >
                {t('reports.confirm_mute')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Report Modal */}
      {rejectTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setRejectTarget(null)}
        >
          <div
            className="bg-white rounded-xl p-6 w-[480px] max-w-[90vw] relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="ReportManagement_List_rejectModal"
          >
            <button
              onClick={() => setRejectTarget(null)}
              className="absolute top-4 right-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
              data-testid="ReportManagement_List_rejectModalCloseButton"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold text-gray-900">
              {t('reports.reject_title')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('reports.reject_desc', {
                name: rejectTarget.reportedUser?.displayName || '—',
              })}
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t('reports.reject_notice_title')}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {t('reports.reject_notice_text')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.reject_reason_label')}
              </label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('reports.reject_reason_placeholder')}
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 resize-none"
                data-testid="ReportManagement_List_rejectModalReasonTextArea"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setRejectTarget(null)}
                data-testid="ReportManagement_List_rejectModalCancelButton"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  handleUpdateStatus(rejectTarget.id, 'rejected');
                  setRejectTarget(null);
                  setSelected(null);
                }}
                disabled={actionLoading}
                data-testid="ReportManagement_List_rejectModalConfirmButton"
              >
                {t('reports.confirm_reject')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Overlay */}
      {previewImage && (
        <ImagePreviewOverlay
          src={previewImage}
          onClose={() => setPreviewImage(null)}
          testId="ReportManagement_List_imagePreviewModal"
        />
      )}
    </div>
  );
};

export default ReportManagement;
